<script setup lang="ts">
import type { GameVehicleSummary } from "~~/types/gamedata";

/**
 * Schiffsbild aus der Star Citizen Wiki API für den öffentlichen ShipExkurs.
 *
 * Natives `<img>` statt `<NuxtImg>`: `@nuxt/image` ist in diesem Projekt global
 * auf den `directus`-Provider fixiert (siehe `nuxt.config.ts`), der jedem `src`
 * `https://assets.ariscorp.de/` voranstellt — bricht bei externen Wiki-URLs
 * (`media.starcitizen.tools`). Gleiches Muster wie `AMSUiShipImage`.
 */
const props = withDefaults(
  defineProps<{
    vehicle?: GameVehicleSummary | null;
    /** `thumbnail` (600px, WebP) für Listen/Karten, `url` für große Ansichten. */
    size?: "thumbnail" | "original";
    alt?: string;
    imgClass?: string;
  }>(),
  { size: "thumbnail" },
);

const source = computed(() => {
  const image = props.vehicle?.image;
  if (!image) return null;
  return props.size === "original" ? image.url : image.thumbnail;
});

const label = computed(
  () => props.alt ?? props.vehicle?.fullName ?? props.vehicle?.name ?? "Unbekanntes Schiff",
);
</script>

<template>
  <img
    v-if="source"
    :src="source"
    :alt="label"
    :width="vehicle?.image?.width ?? undefined"
    :height="vehicle?.image?.height ?? undefined"
    loading="lazy"
    :class="imgClass"
  >
  <div
    v-else
    :class="[
      'flex flex-col items-center justify-center gap-1 bg-bunker-900/60 text-center',
      imgClass,
    ]"
    :aria-label="`Kein Bild für ${label} verfügbar`"
  >
    <UIcon name="i-lucide-rocket" class="size-6 text-industrial-500/50" />
    <span class="text-xs font-medium text-neutral-500">
      {{ vehicle?.manufacturer?.short ?? "Kein Bild" }}
    </span>
  </div>
</template>
